import { useEffect, useState } from 'react';
import styles from './UploadFileProgressModal.module.scss';
import icons from '../assets/icons';
import { ProgressBarLine } from './gamificationTooltip/gamificationTooltip';
import { ElipsisTooltip } from '../common/ElipsisTooltip';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectCompletedFiles,
  selectUploadCompleted,
  selectUploadedFiles,
  setCompletedFiles,
  setUploadedFiles,
  updateFile,
  patchUploadedFiles,
} from '../redux/slicers/appSlicer';
import {
  apiBulkUploadTextures,
  cancellableApiPostImageOnPresignedData,
  apiDeleteUpload3DModel,
  apiBulkDeleteTextures,
  apiPost3DModelJob,
  apiBulkUpload3DModel,
  apiBulkDeleteModels,
  apiUploadProductImagesV2,
  apiMultiDeleteProductImagesAndGif,
  apiPostProductVideos,
  PRODUCT_V2_VIDEO_UPLOAD_URL,
  apiDeleteProductVideos,
  apiPostImageOnPresignedData,
  apiPatchProductVideo,
  apiUploadTextureImagesV2,
  apiMultiDeleteTextureImagesAndGif,
  PRODUCT_V2_AI_VIDEO_UPLOAD_URL,
} from '../helpers/api';
import {
  clearCacheFromAPI,
  createImageThumbnail,
  createModelAutomationRender,
  createVideoThumbnail,
  getExtensionFromURL,
  getImageDetails,
  isTextureProductPage,
  sendModelViewerExceptionToSentry,
  wait,
  windowOpenV2,
} from '../helpers/jsHelper';
const {
  ImagineCheckedIcon,
  PauseInCircleIcon,
  CancelInCircleIcon,
  ArrowDownIcon,
  CircleIcon,
  CrossIcon,
  RefreshIconV2,
} = icons;
import cx from 'classnames';
import {
  ACCEPT_3D_MODELS,
  ACCEPT_3D_MODELS_IN_ZIP,
  ACCEPTABLE_IMAGE_TYPE,
  DIMENSION_SHOT_VALUE,
  GIF_VALUE,
  MODEL_SIZE_LIMIT,
  PRODUCT_LIFESTYLE_VIDEO,
  PRODUCT_PAGE_FILE_SUPPORTED_TYPES,
  PRODUCT_PAGE_GIF_SUPPORTED_TYPES,
  PRODUCT_PAGE_IMAGE_SUPPORTED_TYPES,
  PRODUCT_PAGE_VIDEO_SUPPORTED_TYPES,
  STATUS,
  STATUS_DONE_KEY,
} from '../constants/constants';
import { getExtension } from './3DModelUpload/Upload3DModalButton';
import {
  persistPendingUpload,
  markFileUploaded,
  markUploadAlive,
  clearUploadBeat,
  createStallWatcher,
  completeUpload,
  discardPendingUpload,
  resumeOneUploadNow,
  RESUME_OUTCOME,
} from '../helpers/resumableModelUpload';
import { unzip } from 'unzipit';
import ArrowDownIconV2 from './icons/ArrowDownIconV2';
import {
  ADMIN_DASHBOARD,
  ADMIN_MY_PRODUCTS_3DMODAL,
  ADMIN_MY_PRODUCTS_3DMODAL_EACH,
  ADMIN_MY_PRODUCTS_3DMODAL_EACH_V2,
  ADMIN_MY_PRODUCTS_3DMODAL_V2,
  ADMIN_MY_PRODUCTS_TEXTURE_EDIT,
  ADMIN_MY_PRODUCTS_TEXTURE_V2,
  ADMIN_MY_PRODUCTS_TEXTURES_EACH_V2,
  COMMON_HOME,
} from '../constants/routePathConstants';
import ModelLimitUpgrade from './popups/ModelLimitUpgrade';
import { UploadTextureModelSelect } from './UploadTextureModelSelect';
import { dispatcher, navigate } from '../helpers/projectHelper';
import { useInfoDialogaActions } from '../hooks/useInfoDialogaActions';
import {
  selectActiveView,
  setFetchModels,
  setFetchTextures,
  setGenerateVisualModal,
  setTriggerProductContentUpdate,
} from '../redux/slicers/productV2Slicer';
import { getAppState } from '../redux/store';
import { setClientSubscriptionPlans } from '../api/subscription/subscription';
import { Mixpanel, MxEvent } from '../common/Mixpanel';
import { updatePublicLinkProductTexture } from '../screens/NewProductUIV2/ShareButtonV2/helpers';
import { PERMISSION } from '../constants/permissionConstants';
import { useStaticPermissions } from '../hooks/usePermissions';
import { useUserContext } from '../contexts/UserContext';
import { convertBytesInMB } from '../helpers/unityHelper';

export const FILE_STATUS = {
  DONE: 1,
  FAILED: 2,
  UPLOADING: 3,
  UNSUPPORTED: 4,
  RETRYING: 5, // failed, but a resume record exists so it will be picked up again
};

// How long a row may sit in RETRYING before we stop promising and show a plain failure. Resume does
// not even run while offline, so an attempt counter would stay at 0 and the row would say
// "Retrying…" forever — elapsed time is the only honest measure here. Note this only changes the
// LABEL: background resume keeps going and will still flip the row to complete if it lands later.
const RETRY_GIVE_UP_MS = 2 * 60 * 1000;
const RETRY_GIVE_UP_CHECK_MS = 10000;

const fetchProductCategoryItems = (id = null, isTexturePage = false) => {
  if (!id) return null;
  setTimeout(() => {
    dispatcher(setTriggerProductContentUpdate({ id }));
    dispatcher(setClientSubscriptionPlans());
    updatePublicLinkProductTexture({ productId: id, isTexture: isTextureProductPage() });
  }, 1000);
};

const fetchModels = (id = null) => {
  setTimeout(() => {
    dispatcher(setFetchModels(id));
    dispatcher(setClientSubscriptionPlans());
  }, 1000);
};

const fetchTextures = (id = null) => {
  setTimeout(() => {
    dispatcher(setFetchTextures(id));
    dispatcher(setClientSubscriptionPlans());
  }, 3000);
};

export const UploadFileProgressModal = ({ currentUser }) => {
  const [showItems, setShowItems] = useState(true);
  const [limit, setLimit] = useState({});
  const [progress, setProgress] = useState({}); // Using this to update the progress bar for now
  const {
    files: uploadedFiles,
    product,
    model,
    texture,
    hideArrow,
  } = useSelector(selectUploadedFiles);
  const [showApplyTextureModal, setShowApplyTextureModal] = useState(false);
  const [savedUploadFileData, setSavedUploadedFileData] = useState([]);
  const [isOffline, setIsOffline] = useState(navigator?.onLine === false);
  const isTexturePage = isTextureProductPage();
  const { hasPermission } = useStaticPermissions();
  const { isGuestUser } = useUserContext();

  const dispatch = useDispatch();

  const isUploadCompleted = useSelector(selectUploadCompleted);
  const { openInfoDialog, closeInfoDialog } = useInfoDialogaActions();

  const fileUploaded = (file) => file?.progress === 100;
  const completedFiles = () =>
    Object.values(uploadedFiles)?.filter((file) => file?.progress === 100)?.length;
  const totalFiles = () => Object.keys(uploadedFiles)?.length;
  const totalProgress = () => {
    return `${completedFiles()}/${totalFiles()}`;
  };

  const allFilesUploaded = () => completedFiles() === totalFiles();

  useEffect(() => {
    // fetching product/texture category items when upload is completed
    if (isUploadCompleted) {
      fetchProductCategoryItems(product, isTexturePage);

      Object.values(uploadedFiles)?.forEach((file) => {
        const ext = getExtension(file?.name);
        Mixpanel.track(true, MxEvent.FILE_UPLOAD_SUCCESS, {
          file_type: ACCEPT_3D_MODELS.includes(`.${ext}`) ? 'Model' : 'Texture',
          file_extension: ext,
          file_name: file?.name?.split('.')?.[0],
        });
      });

      if (model) fetchModels(Object.keys(uploadedFiles)?.[0]);

      if (texture) fetchTextures(Object.keys(uploadedFiles)?.[0]);
    }
  }, [isUploadCompleted]);

  useEffect(() => {
    return () => {
      setSavedUploadedFileData([]);
      setShowApplyTextureModal(false);
      dispatch(setUploadedFiles({}));
    };
  }, []);

  // Only used to word the retrying label — resume can't run at all while offline, so saying just
  // "Retrying…" there would look stuck.
  useEffect(() => {
    const sync = () => setIsOffline(navigator?.onLine === false);
    window.addEventListener('online', sync);
    window.addEventListener('offline', sync);
    return () => {
      window.removeEventListener('online', sync);
      window.removeEventListener('offline', sync);
    };
  }, []);

  const deleteAPI = (formData, isDimensionShot, isGIF) => {
    if (product) {
      const data = {};
      data.ids = formData.getAll('id');

      if (!isGIF) data.is_dimension_shot = isDimensionShot;
      const func = isTexturePage
        ? apiMultiDeleteTextureImagesAndGif
        : apiMultiDeleteProductImagesAndGif;
      func({ id: product, data: data });
    } else {
      apiBulkDeleteTextures(formData);
    }
  };

  const deleteVideoAPI = ({ file }) => {
    const videoId = file.data.id;
    const url =
      file.option?.value === PRODUCT_LIFESTYLE_VIDEO
        ? PRODUCT_V2_VIDEO_UPLOAD_URL
        : PRODUCT_V2_AI_VIDEO_UPLOAD_URL;
    apiDeleteProductVideos({ url: `${url}${videoId}/` });
  };

  const uploadImage = (images) => (product ? uploadImages(images) : createTextures(images));

  const handleCancelAll = () => {
    const _files = Object.values(uploadedFiles);

    const pendingFiles = _files.filter(
      (file) => file?.data && (!fileUploaded(file) || file?.status === FILE_STATUS.FAILED)
    );
    const texturesFormData = new FormData();
    const dimensionsFormData = new FormData();
    const GIFFormData = new FormData();
    const modelsFormData = new FormData();

    for (const file of pendingFiles) {
      file.controller?.abort();
      if (file?.data?.id) {
        if (file.isImage) {
          if (product) {
            if (file?.isGIF) {
              GIFFormData.append('id', file?.data?.id);
              continue;
            }

            const isDimensionShot = file?.isDimensionShot;

            if (isDimensionShot) {
              dimensionsFormData.append('id', file?.data?.id);
              continue;
            }
          }
          texturesFormData.append('id', file?.data?.id);
        } else if (file.isVideo) {
          deleteVideoAPI({ file: file });
        } else {
          modelsFormData.append('id', file?.data?.id);
        }
      }
    }

    if (Array.from(texturesFormData.keys())?.length) {
      deleteAPI(texturesFormData);
    }

    if (Array.from(dimensionsFormData.keys())?.length) {
      deleteAPI(dimensionsFormData, true);
    }

    if (Array.from(GIFFormData.keys())?.length) {
      deleteAPI(GIFFormData, false, true);
    }

    if (Array.from(modelsFormData.keys())?.length) {
      apiBulkDeleteModels(modelsFormData);
      // Drop the resumable records so a cancelled upload isn't resurrected on resume.
      modelsFormData.getAll('id').forEach((modelId) => discardPendingUpload(modelId));
    }

    dispatch(setUploadedFiles({}));
  };

  const handleCancelSingleFile = (file) => {
    const { [file?.id]: _, ..._uploadedFiles } = { ...uploadedFiles };
    file.controller?.abort();

    if (file?.data?.id) {
      if (file.isImage) {
        const formData = new FormData();
        formData.append('id', file?.data?.id);

        deleteAPI(formData, file?.isDimensionShot, file?.isGIF);
      }
      if (file.isVideo) {
        deleteVideoAPI({ file: file });
      } else {
        apiDeleteUpload3DModel(file?.data?.id);
        // Drop the resumable record so a cancelled upload isn't resurrected on resume.
        if (file?.isModel) discardPendingUpload(file?.data?.id);
      }
    }
    dispatch(setUploadedFiles(_uploadedFiles));
  };

  const handleOpenFile = (file) => {
    // ADMIN_MY_PRODUCTS_TEXTURES_EACH_V2
    window.open(
      file?.isImage
        ? ADMIN_MY_PRODUCTS_TEXTURE_EDIT + `?textureId=${file?.data?.id}`
        : ADMIN_MY_PRODUCTS_3DMODAL_EACH_V2.replace(':id', file?.data?.id)
    );
  };

  const uploadFileToS3 = async ({ presignedData, file, onDownloadCallback }) => {
    const controller = new AbortController();
    const { signal } = controller;

    file.controller = controller;
    // Losing the network doesn't reliably fail an in-flight request — the socket can stay open long
    // after the bytes stopped. Abort a transfer that stops moving so it doesn't linger as a zombie.
    file.stalled = false; // reset: the same file object is reused when the user hits Retry
    const stallWatcher = createStallWatcher(() => {
      file.stalled = true;
      controller.abort();
    });
    try {
      const _response = await cancellableApiPostImageOnPresignedData(
        file,
        presignedData,
        (event) => {
          stallWatcher.progress(event);
          const _progress = parseInt((event.loaded / event.total) * 100);
          file.progress = _progress < 100 ? _progress : 99;
          // Keeping the progress at 99 as we need to send render apis also.
          dispatch(updateFile(file));
          setProgress((prev) => ({ ...prev, [file.id]: file.progress }));
          // Keep the resume record claimed while this transfer is live, otherwise the 10s resume
          // poll can't tell it from a dead upload and starts a duplicate one.
          if (file?.isModel) markUploadAlive(file?.data?.id);
        },
        signal
      );

      if ([200, 201, 202, 203, 204, 205].includes(_response?.status)) {
        dispatch(setCompletedFiles(file?.id));
        file.status = FILE_STATUS.DONE;
        file.progress = 100;
        dispatch(updateFile(file));
        setProgress((prev) => ({ ...prev, [file.id]: file.progress }));
        onDownloadCallback?.();

        if (!product) {
          if (file?.isModel) {
            // console.log('DEBUG___file', file);
            // File is in S3 — only the job-send remains (resume state).
            markFileUploaded(file?.data?.id);

            const formData = new FormData();
            formData.append('product', file?.data?.id);
            formData.append('status', 0);
            formData.append('generate_ar', false);

            // Removing .skp file support
            // if (getExtensionFromURL(file?.data?.prop_data_user_file) === '.skp') {
            //   // Skp File render
            //   formData.append('is_skp', true);
            // }

            // Delete the resumable record only after the job-send actually succeeds.
            // NOTE: the api() wrapper RESOLVES with `undefined` on a network error (it doesn't
            // reject), so we must check for a real response — not just that the promise resolved —
            // otherwise an offline job-send would wrongly delete the record.
            apiPost3DModelJob(formData)
              .then((jobRes) => {
                if (jobRes) completeUpload(file?.data?.id);
                // else: keep record at job_pending; resume will re-send the job when online
              })
              .catch(() => {
                /* keep record at job_pending; resume will re-send the job */
              });
          }
          // else {
          const files = Object.values(uploadedFiles || {})?.filter(
            (file) => file.isImage || file.isModel
          );
          // const allFilesUploaded =
          //   files?.length &&
          //   files?.length ===
          //   files?.filter((file) => file?.data && file?.status === FILE_STATUS.DONE)?.length;

          const canAccessV2Page = isGuestUser() || hasPermission(PERMISSION.PRODUCT_PAGE_V2);

          // Allow creating a job/generation for successfully uploaded files.
          // This applies in cases where some files have failed due to errors or an insufficient count of textures/models.
          const allFilesUploadedSuccessfully =
            files?.length &&
            files?.filter((file) => file?.data && file?.status === FILE_STATUS.UPLOADING)
              ?.length === 0;

          if (canAccessV2Page && allFilesUploadedSuccessfully) {
            const textures = files?.filter(
              (file) => file.isImage && file?.status === FILE_STATUS.DONE
            );
            const models = files?.filter(
              (file) => file.isModel && file?.status === FILE_STATUS.DONE
            );

            // If there is only one 3D model and no textures, opens the individual 3D model page.
            // Sends render requests for automation of the uploaded 3D models.
            if (models?.length === 1 && textures?.length === 0) {
              if (
                (window.location.pathname === COMMON_HOME ||
                  window.location.pathname === ADMIN_MY_PRODUCTS_3DMODAL_V2 ||
                  window.location.pathname.includes(ADMIN_DASHBOARD)) &&
                models?.length
              ) {
                // New code for automation of product page added here
                // await createModelAutomationRender({
                //   data: models?.[0]?.data,
                //   showMessageBeforeJobSend: true,
                //   successMessage: 'Your visuals are being generated',
                // });
               dispatch(setGenerateVisualModal(models?.[0]?.data))
              }
            }

            // handling job send case when only one texture is uploaded
            else if (textures?.length === 1 && models?.length === 0) {
              if (
                (window.location.pathname === COMMON_HOME ||
                  window.location.pathname.includes(ADMIN_DASHBOARD) ||
                  window.location.pathname.includes(ADMIN_MY_PRODUCTS_TEXTURE_V2)) &&
                textures?.length
              ) {
                setShowApplyTextureModal(true);
                dispatch(setUploadedFiles({}));
                // closeInfoDialog();
              }
            } else {
              // handling job send case when multiple models / textures are uploaded
              Mixpanel.track(true, MxEvent.POPUP_GENERATE_VISUAL);

              openInfoDialog({
                title: 'Files uploaded successfully!',
                description: `${
                  files?.filter((f) => f.status === FILE_STATUS.DONE)?.length
                } new file${
                  files?.length > 1 ? 's' : ''
                } have been successfully uploaded. Would you like to generate visual${
                  files?.length > 1 ? 's' : ''
                } now?`,
                buttonText: `Generate Visual${files?.length > 1 ? 's' : ''}`,
                onButtonClick: async () => {
                  Mixpanel.track(true, MxEvent.CLICK_GENERATE_VISUAL);

                  if (models?.length > 1 && !textures?.length){
                    dispatch(setGenerateVisualModal(models?.map((list)=> list?.data)))
                  }
                  /**
                   * Handles the automation of product pages after successful upload of 3D models.
                   * - If there are multiple 3D models and no textures, opens the 3D model list page.
                   * - Sends render requests for automation of the uploaded 3D models.
                   */
                  // Promise.allSettled(
                  //   models?.map(async (_item, i) => {
                  //     // New code for automation of product page added here
                  //     await createModelAutomationRender({
                  //       data: _item?.data,
                  //       preventRedirection: true,
                  //       showMessage: false, // i === models?.length - 1,
                  //       //  successMessage: 'Your visuals are being generated', // TODO: Update success message when we get it for uploading multiple models/textures
                  //     });
                  //   })
                  // );
                  // if (
                  //   (window.location.pathname === COMMON_HOME ||
                  //     window.location.pathname === ADMIN_MY_PRODUCTS_3DMODAL_V2 ||
                  //     window.location.pathname.includes(ADMIN_DASHBOARD)) &&
                  //   models?.length
                  // ) {
                  //   if (window.location.pathname !== ADMIN_MY_PRODUCTS_3DMODAL_V2) {
                  //     if (models?.length > 1 && !textures?.length) {
                  //       navigate(ADMIN_MY_PRODUCTS_3DMODAL_V2, '_self');
                  //     }
                  //   }
                  // }
                  /**
                   * Checks if the current page is the home page, dashboard, or the texture page, and if there are any uploaded textures.
                   * If the conditions are met, it sets the `showApplyTextureModal` state to true, clears the `uploadedFiles` state, and closes the info dialog.
                   */
                  if (
                    (window.location.pathname === COMMON_HOME ||
                      window.location.pathname.includes(ADMIN_DASHBOARD) ||
                      window.location.pathname.includes(ADMIN_MY_PRODUCTS_TEXTURE_V2)) &&
                    textures?.length
                  ) {
                    setShowApplyTextureModal(true);
                    dispatch(setUploadedFiles({}));
                    closeInfoDialog();
                  }
                },
                buttonText2: 'Not right now',
                isButton2Cancel: true,
                onButtonClick2: () => {
                  Mixpanel.track(true, MxEvent.CLICK_SKIP_EXPERIENCE_CREATION);

                  closeInfoDialog();
                },
                addOrSeparator: false,
                btnContainerClass: styles.btnContainer,
                closeable: false,
                designV2: true,
                okBtnColor: '#ffffff',
                contentClassName: styles.infoContent,
                dialogClassName: styles.infoDialog,
                topIcon: <ImagineCheckedIcon />,
                designV2Props: {
                  fillBtnClassName: styles.btn,
                  okBtnClassName: `${styles.btn} ${styles.okbtn}`,
                },
              });
            }
          }
        }
      } else if (file.stalled) {
        // A cancel resolves with no response rather than throwing, so the success branch above is
        // simply skipped — without this the row would sit frozen at its last percentage forever.
        // A user-initiated cancel doesn't land here: that path removes the row outright.
        if (file.isModel) {
          sendModelViewerExceptionToSentry({
            message: `Model "${file.name}" upload to s3 stalled and was aborted.`,
            file,
          });
          // Release the claim so resume restarts this as soon as the network is back, instead of
          // waiting out the rest of the heartbeat TTL.
          await clearUploadBeat(file?.data?.id);
        }
        markRowFailed(file);
      }
    } catch (error) {
      if (file.isModel) {
        sendModelViewerExceptionToSentry({
          message: `Model "${file.name}" upload to s3 failed.`,
          file,
        });
      }
      markRowFailed(file);
    } finally {
      stallWatcher.stop();
    }
  };

  /**
   * Show a failed upload honestly: a resumable model is about to be retried in the background, so
   * calling it "Failed" (with a retry button that duplicates the job) is a lie. Anything that is
   * NOT resumable — textures, or a model whose record couldn't be persisted — really is final.
   */
  const markRowFailed = (file) => {
    if (file?.resumable) {
      file.status = FILE_STATUS.RETRYING;
      file.retryingSince = Date.now();
      file.errorMessage = '';
    } else {
      file.status = FILE_STATUS.FAILED;
    }
    // Patch this one row rather than writing back the whole map: `uploadedFiles` here is the
    // render-time snapshot, and a background resume may have patched another row since.
    dispatch(updateFile(file));
  };

  const limitReached = (totalCount, fileCounts) => fileCounts > totalCount;

  async function uploadImages(images) {
    const { isDisplayView } = selectActiveView(getAppState());

    for (const image of images) {
      const formData = new FormData();
      const isDimensionShot = image?.option?.value === DIMENSION_SHOT_VALUE;
      const isGIF = image?.type?.includes('gif');
      image.isImage = true;
      image.isGIF = isGIF;
      image.isDimensionShot = isDimensionShot;
      image.file_name = image?.name?.split('.')[0];
      formData.append('name', image?.file_name);
      formData.append(isTexturePage ? 'texture' : 'product', product);
      formData.append('image_file_url_key', image?.name);

      if (isDisplayView) formData.append('show_on_page', true);

      if (!isDimensionShot && !isGIF)
        formData.append(
          isTexturePage ? 'texture_content_type' : 'product_content_type',
          image?.option?.value
        );

      if (isDimensionShot) {
        formData.append('is_dimension_shot', isDimensionShot);
      }

      try {
        const func = isTexturePage ? apiUploadTextureImagesV2 : apiUploadProductImagesV2;
        const response = await func({
          id: product,
          data: formData,
          isGIF: isGIF,
        });

        if (response?.status) {
          const data = response?.data;
          if (data) {
            image.data = data;
            uploadFileToS3({ presignedData: data.presigned_data, file: image });

            // Upload Thumbnail
            if (data?.presigned_data_thumb) {
              const thumbnail = await createImageThumbnail({ file: image });
              apiPostImageOnPresignedData(thumbnail, data?.presigned_data_thumb);
            }
          }
        } else {
          image.status = FILE_STATUS.FAILED;
          image.errorMessage = 'Failed';
          dispatch(setUploadedFiles({ ...uploadedFiles, [image.id]: image }));
        }
      } catch (error) {
        image.status = FILE_STATUS.FAILED;
        image.errorMessage = 'Failed';
        dispatch(setUploadedFiles({ ...uploadedFiles, [image.id]: image }));
      }
    }

    // if (Array.from(formData.keys())?.length) {
    //     try {
    //         const response = await apiUploadProductImagesV2({ id: product, data:formData })

    //         if (response?.status) {
    //             images.forEach(async image => {
    //                 const data = response?.data?.find(data => data.temp_id === image?.id)
    //                 if (data) {
    //                     image.data = data
    //                     uploadFileToS3(data.presigned_data, image)
    //                 }
    //             })
    //         }
    //     }
    //     catch (error) {
    //         let _images = {}
    //         for (const image of images) {
    //             _images[image.id] = image
    //             _images[image.id].status = FILE_STATUS.FAILED
    //             _images[image.id].errorMessage = "Failed"
    //         }
    //         dispatch(setUploadedFiles({ ...uploadedFiles, ..._images }))
    //     }
    // }
  }

  async function uploadVideos(videos) {
    const { isDisplayView } = selectActiveView(getAppState());

    for (const video of videos) {
      video.isVideo = true;
      video.file_name = video?.name?.split('.')[0];

      const data = {
        name: video?.file_name,
        [isTexturePage ? 'assigned_texture' : 'assigned_product']: product,
        description: video?.file_name,
        file_url_key: video?.name,
        is_v2: true,
        ...(isDisplayView ? { show_on_page: true } : {}),
      };
      try {
        const url =
          video.option?.value === PRODUCT_LIFESTYLE_VIDEO
            ? PRODUCT_V2_VIDEO_UPLOAD_URL
            : PRODUCT_V2_AI_VIDEO_UPLOAD_URL;
        const response = await apiPostProductVideos({ url: url, data: data });

        if (response?.status) {
          const data = response?.data;
          if (data) {
            video.data = data;
            uploadFileToS3({
              presignedData: data.presigned_data,
              file: video,
              onDownloadCallback: () => {
                apiPatchProductVideo({ id: data?.id, url: url, data: { status: STATUS_DONE_KEY } });
              },
            });

            // Upload Thumbnail
            if (data?.presigned_data_thumb) {
              const thumbnail = await createVideoThumbnail({ file: video });
              apiPostImageOnPresignedData(thumbnail, data?.presigned_data_thumb);
            }
          }
        } else {
          video.status = FILE_STATUS.FAILED;
          video.errorMessage = 'Failed';
          dispatch(setUploadedFiles({ ...uploadedFiles, [video.id]: video }));
        }
      } catch (error) {
        video.status = FILE_STATUS.FAILED;
        video.errorMessage = 'Failed';
        dispatch(setUploadedFiles({ ...uploadedFiles, [video.id]: video }));
      }
    }

    // if (Array.from(formData.keys())?.length) {
    //     try {
    //         const response = await apiUploadProductImagesV2({ id: product, data:formData })

    //         if (response?.status) {
    //             images.forEach(async image => {
    //                 const data = response?.data?.find(data => data.temp_id === image?.id)
    //                 if (data) {
    //                     image.data = data
    //                     uploadFileToS3(data.presigned_data, image)
    //                 }
    //             })
    //         }
    //     }
    //     catch (error) {
    //         let _images = {}
    //         for (const image of images) {
    //             _images[image.id] = image
    //             _images[image.id].status = FILE_STATUS.FAILED
    //             _images[image.id].errorMessage = "Failed"
    //         }
    //         dispatch(setUploadedFiles({ ...uploadedFiles, ..._images }))
    //     }
    // }
  }

  async function createTextures(textures) {
    const formData = new FormData();
    for (const texture of textures) {
      texture.isImage = true;
      let imageDetails = {};
      try {
        imageDetails = await getImageDetails(URL.createObjectURL(texture));
      } catch (error) {
        console.error(error);
        texture.status = FILE_STATUS.FAILED;
        dispatch(setUploadedFiles({ ...uploadedFiles, [texture?.id]: texture }));
        continue;
      }
      texture.file_name = texture?.name?.split('.')[0];
      formData.append('name', texture?.file_name);
      formData.append('width', imageDetails?.widthInCentimeter);
      formData.append('height', imageDetails?.heightInCentimeter);
      formData.append('rotation', 0);
      formData.append('is_map', false);
      formData.append('texture_file_url_key', texture?.name);
      formData.append('temp_id', texture?.id);
    }

    if (Array.from(formData.keys())?.length) {
      try {
        const response = await apiBulkUploadTextures(formData, '', '');

        if (response?.status) {
          setSavedUploadedFileData(response?.data);
          textures.forEach(async (texture) => {
            const data = response?.data?.find((data) => data.temp_id === texture?.id);
            if (data) {
              texture.data = data;
              uploadFileToS3({ presignedData: data.presigned_data, file: texture });
            }
          });
        }
      } catch (error) {
        // errorToastV2(error?.message)
        const availbleLimit = error?.data?.available_texture_count;
        const isLimitReached =
          availbleLimit !== undefined ? limitReached(availbleLimit, textures?.length) : false;

        if (isLimitReached && !limit?.textures)
          setLimit((prev) => ({ ...prev, textures: availbleLimit }));

        let _textures = {};
        let i = 0;
        for (const texture of textures) {
          _textures[texture.id] = texture;
          if (isLimitReached) {
            i++;

            if (i <= availbleLimit) {
              _textures[texture.id].status = null;
              _textures[texture.id].reuploading = true;
              continue;
            }
          }
          _textures[texture.id].status = FILE_STATUS.FAILED;
          _textures[texture.id].errorMessage = isLimitReached
            ? 'Texture upload limit exceeded'
            : 'Failed';

          if (isLimitReached) {
            const ext = getExtension(texture?.name);
            Mixpanel.track(true, MxEvent.UPLOAD_LIMIT_REACHED_POPUP, {
              file_type: 'Texture',
              file_extension: ext,
              file_name: texture?.file_name,
            });
          }
        }

        dispatch(setUploadedFiles({ ...uploadedFiles, ..._textures }));
      }
    }
  }

  async function createModels(models) {
    const formData = new FormData();
    for (const model of models) {
      model.isModel = true;
      model.file_name = model?.name?.split('.')[0];
      formData.append('name', model?.file_name);
      formData.append('main_asset_name', model?.file_name);
      formData.append(
        'access_token',
        'it does not work without token :( waiting for backend update'
      );
      formData.append('upload_type', 1);
      formData.append('prop_data_user_file_name', model?.name);
      formData.append('temp_id', model?.id);
    }

    const uploadModels = async (response) => {
      if (response?.status) {
        // setSavedUploadedFileData(response.data);
        await Promise.all(
          models.map(async (model) => {
            const data = response?.data?.find((_data) => _data.temp_id === model?.id);
            if (data) {
              model.data = data;
              // Persist so the upload can be resumed if interrupted before completion. Remember
              // whether it actually persisted: it silently skips when storage is full, and only a
              // persisted file can honestly be shown as "Retrying…" after a failure.
              model.resumable = await persistPendingUpload({
                productId: data?.id,
                file: model,
                productData: data,
                meta: {
                  tempId: model?.id,
                  name: model?.file_name,
                  filename: model?.name,
                  extension: getExtension(model?.name),
                  status: 0,
                  orgId: currentUser?.organization,
                  userId: currentUser?.user?.id,
                  now: Date.now(),
                },
              });
              uploadFileToS3({ presignedData: data?.presigned_url_prop_user_file, file: model });
            }
          })
        );
      }
    };

    if (Array.from(formData.keys())?.length) {
      try {
        clearCacheFromAPI();
        const response = await apiBulkUpload3DModel(formData);
        uploadModels(response);
      } catch (error) {
        // errorToastV2(error?.message)
        const availbleLimit = error?.available_limit;
        const isLimitReached =
          availbleLimit !== undefined ? limitReached(availbleLimit, models?.length) : false;

        if (isLimitReached && !limit?.models)
          setLimit((prev) => ({ ...prev, models: availbleLimit }));
        let _models = {};
        let i = 0;
        for (const model of models) {
          _models[model.id] = model;
          if (isLimitReached) {
            i++;
            if (i <= availbleLimit) {
              _models[model.id].status = null;
              _models[model.id].reuploading = true;
              continue;
            }
          }
          _models[model.id].status = FILE_STATUS.FAILED;
          _models[model.id].errorMessage = isLimitReached
            ? 'Model upload limit exceeded'
            : 'Failed';

          if (isLimitReached) {
            sendModelViewerExceptionToSentry({
              message: `Model upload limit reaached`,
              file: model,
            });
            const ext = getExtension(model?.name);
            Mixpanel.track(true, MxEvent.UPLOAD_LIMIT_REACHED_POPUP, {
              file_type: 'Model',
              file_extension: ext,
              file_name: model?.file_name,
            });
          } else {
            sendModelViewerExceptionToSentry({ message: error?.message, file: model });
          }
        }

        dispatch(setUploadedFiles({ ...uploadedFiles, ..._models }));
      }
    }
  }

  const handleRetry = async (file) => {
    // A resumable model is owned by the resume machinery, so route the retry through it instead of
    // re-running the upload here. That reuses its guards: if the background run already finished
    // this one we just mark the row done (no second job), and an actual retry gets a FRESH presigned
    // URL — the one cached on file.data was minted before the failure and has likely expired.
    if (file?.resumable && file?.data?.id) {
      file.status = FILE_STATUS.RETRYING;
      file.retryingSince = Date.now();
      file.errorMessage = '';
      dispatch(updateFile(file));

      const result = await resumeOneUploadNow(file?.data?.id);
      if (result?.outcome === RESUME_OUTCOME.DONE) {
        file.status = FILE_STATUS.DONE;
        file.progress = 100;
        dispatch(updateFile(file));
      } else if (result?.outcome === RESUME_OUTCOME.GONE) {
        file.status = FILE_STATUS.FAILED;
        file.errorMessage = 'Upload failed';
        dispatch(updateFile(file));
      }
      // RETRY -> leave the row in RETRYING; the background poll keeps trying.
      return;
    }

    if (file?.data) {
      if (file?.isImage) {
        uploadFileToS3({ presignedData: file?.data?.presigned_data, file });
      } else {
        uploadFileToS3({ presignedData: file?.data?.presigned_url_prop_user_file, file });
      }
    } else {
      if (file?.isImage) {
        uploadImage([file]);
      } else if (file?.isVideo) {
        uploadVideos([file]);
      } else createModels([file]);
    }

    file.status = FILE_STATUS.UPLOADING;
    dispatch(setUploadedFiles({ ...uploadedFiles, [file?.id]: file }));
  };

  // Stop promising a retry that has been pending too long (see RETRY_GIVE_UP_MS). Runs only while
  // something is actually retrying, and stops itself as soon as nothing is.
  useEffect(() => {
    const isRetrying = (file) => file?.status === FILE_STATUS.RETRYING;
    if (!Object.values(uploadedFiles || {}).some(isRetrying)) return;

    const timer = setInterval(() => {
      const expired = Object.values(uploadedFiles || {}).filter(
        (file) => isRetrying(file) && Date.now() - (file.retryingSince || 0) >= RETRY_GIVE_UP_MS
      );
      if (!expired.length) return;
      dispatch(
        patchUploadedFiles(
          expired.map((file) => ({
            id: file.id,
            status: FILE_STATUS.FAILED,
            errorMessage: 'Upload failed',
          }))
        )
      );
    }, RETRY_GIVE_UP_CHECK_MS);

    return () => clearInterval(timer);
  }, [uploadedFiles]);

  const allReuploadedFileUploaded = () => {
    const files = Object.values(uploadedFiles)?.filter(
      (file) => file?.reuploading && !fileUploaded(file)
    );
    return files?.length === 0;
  };

  const isZipFileValid = async ({ file }) => {
    const { entries } = await unzip(file);
    let filePathList = Object.keys(entries).filter((filePath) => {
      return !filePath.includes('__MACOSX');
    });

    filePathList = filePathList.map((path) => path.replace(/^.*[\\/]/, ''));

    const allowedFileExt = filePathList.filter((f) => {
      if (f.trim() === '') {
        return false;
      }
      return ACCEPT_3D_MODELS_IN_ZIP.includes(getExtension(f));
    });

    return allowedFileExt.length === 1;
  };

  const modelUnderSizeLimit = (file) => {
    const size = convertBytesInMB(file?.size || file?.sizeBytes || file?.bytes);
    return size <= MODEL_SIZE_LIMIT;
  };

  useEffect(() => {
    async function uploadFiles() {
      const files = Object.values(uploadedFiles || {})?.filter(
        (file) => !file?.data && !file?.status
      );
      if (files?.length) {
        const textures = [];
        const models = [];
        const videos = [];

        for (const file of files) {
          const extension = `.${getExtension(file?.name)}`;
          if (product) {
            if (
              `${PRODUCT_PAGE_IMAGE_SUPPORTED_TYPES}, ${PRODUCT_PAGE_GIF_SUPPORTED_TYPES}`.includes(
                extension
              )
            ) {
              file.status = FILE_STATUS.UPLOADING;
              textures.push(file);
            }

            if (PRODUCT_PAGE_VIDEO_SUPPORTED_TYPES.includes(extension)) {
              file.status = FILE_STATUS.UPLOADING;
              videos.push(file);
            }
          } else {
            if (ACCEPTABLE_IMAGE_TYPE.includes(file?.type)) {
              file.status = FILE_STATUS.UPLOADING;
              textures.push(file);
            } else if (extension === '.zip') {
              const isValid = await isZipFileValid({ file: file });

              if (!modelUnderSizeLimit(file)) {
                sendModelViewerExceptionToSentry({
                  message: `File "${file.name}" is too large. Maximum file size is ${MODEL_SIZE_LIMIT} MB.`,
                  file,
                });
                file.status = FILE_STATUS.FAILED;
                file.errorMessage = `File "${file.name}" is too large. Maximum file size is ${MODEL_SIZE_LIMIT} MB.`;
                dispatch(setUploadedFiles({ ...uploadedFiles, [file?.id]: file }));
              } else {
                if (isValid) {
                  file.status = FILE_STATUS.UPLOADING;
                  models.push(file);
                } else {
                  sendModelViewerExceptionToSentry({
                    message: `File "${file.name}" is not supported.`,
                    file,
                  });
                  file.status = FILE_STATUS.UNSUPPORTED;
                  file.errorMessage = 'Unsupported format in zip';
                  dispatch(setUploadedFiles({ ...uploadedFiles, [file?.id]: file }));
                }
              }
            } else if (ACCEPT_3D_MODELS.includes(extension)) {
              if (!modelUnderSizeLimit(file)) {
                sendModelViewerExceptionToSentry({
                  message: `File "${file.name}" is too large. Maximum file size is ${MODEL_SIZE_LIMIT} MB.`,
                  file,
                });
                file.status = FILE_STATUS.FAILED;
                file.errorMessage = `File "${file.name}" is too large. Maximum file size is ${MODEL_SIZE_LIMIT} MB.`;
                dispatch(setUploadedFiles({ ...uploadedFiles, [file?.id]: file }));
              } else {
                file.status = FILE_STATUS.UPLOADING;
                models.push(file);
              }
            } else {
              sendModelViewerExceptionToSentry({
                message: `File "${file.name}" is not supported.`,
                file,
              });
              file.status = FILE_STATUS.UNSUPPORTED;
              dispatch(setUploadedFiles({ ...uploadedFiles, [file?.id]: file }));
            }
          }
        }

        if (textures?.length) {
          // if (!product && textures?.length > 1) {
          //   errorToastV2('Please upload single texture at a time.');
          //   dispatch(setUploadedFiles({}));
          //   return;
          // }
          uploadImage(textures);
        }

        if (videos?.length) uploadVideos(videos);

        if (models?.length) createModels(models);
      }
    }

    uploadFiles();
  }, [uploadedFiles]);

  useEffect(() => {
    dispatch(setUploadedFiles({}));
    setLimit({});
  }, [currentUser?.organization]);

  const showModelLimitPopup = () =>
    allReuploadedFileUploaded() && (limit?.models !== undefined || limit?.textures !== undefined);

  return (
    <>
      {Object.keys(uploadedFiles || {})?.length ? (
        <div className={styles.uploadMoalContainer}>
          <div>
            <header className={styles.header} style={!showItems ? { borderRadius: '8px' } : {}}>
              <div className="d-flex align-items-center justify-content-between w-100">
                <span>
                  {allFilesUploaded()
                    ? 'Upload complete'
                    : `Uploading ${totalFiles()} file${totalFiles() > 1 ? 's' : ''}`}
                </span>
                <div className="d-flex gap-2 align-items-center">
                  {/* <PauseInCircleIcon style={{ color: "#7E7E7E" }} /> */}
                  {allFilesUploaded() ? (
                    <CrossIcon color="#ffffff" onClick={() => dispatch(setUploadedFiles({}))} />
                  ) : (
                    <CancelInCircleIcon style={{ color: '#7E7E7E' }} onClick={handleCancelAll} />
                  )}
                  <ArrowDownIcon
                    style={{ transform: showItems ? '' : 'rotate(180deg)' }}
                    onClick={() => setShowItems(!showItems)}
                  />
                </div>
              </div>
              <div className="w-100 d-flex flex-column gap-2">
                <span
                  className={cx(styles.uploadedTitle, allFilesUploaded() ? styles.success : '')}>
                  {totalProgress()} uploaded
                </span>
                <ProgressBarLine
                  className={cx(styles.progressBar, allFilesUploaded() ? styles.success : '')}
                  progress={(completedFiles() * 100) / totalFiles() || 0}
                />
              </div>
            </header>

            {showItems ? (
              <body className="d-flex flex-column gap-4">
                {Object.values(uploadedFiles || {})?.map((file) => (
                  <div
                    key={file.id}
                    className="d-flex align-items-center justify-content-between w-100 gap-3">
                    <div className="d-flex flex-column gap-2" style={{ width: '80%' }}>
                      <ElipsisTooltip title={file?.name}>
                        {(props) => (
                          <span className={styles.fileName} {...props}>
                            {file?.name}
                          </span>
                        )}
                      </ElipsisTooltip>
                      {file?.status === FILE_STATUS.FAILED ? (
                        <span className={styles.error}>{file?.errorMessage || 'Failed'}</span>
                      ) : file?.status === FILE_STATUS.RETRYING ? (
                        // Neutral, not red: nothing is lost yet — resume will finish this off.
                        <span className={styles.uploadedTitle}>
                          {isOffline ? 'Retrying — waiting for network…' : 'Retrying…'}
                        </span>
                      ) : file?.status === FILE_STATUS.UNSUPPORTED ? (
                        <span className={styles.error}>
                          {file.errorMessage || 'Unsupported file'}
                        </span>
                      ) : (
                        <>
                          <div className="d-flex gap-1 align-items-center">
                            <span
                              className={cx(
                                styles.uploadedTitle,
                                fileUploaded(file) ? styles.success : ''
                              )}>
                              {file?.progress || 0}%
                            </span>
                            {fileUploaded(file) ? (
                              <span
                                className={cx(
                                  styles.uploadedTitle,
                                  styles.success,
                                  'd-flex align-items-center gap-1'
                                )}>
                                <CircleIcon />
                                Upload complete
                              </span>
                            ) : null}
                          </div>
                          {!fileUploaded(file) ? (
                            <ProgressBarLine
                              className={cx(
                                styles.progressBar,
                                fileUploaded(file) ? styles.success : ''
                              )}
                              progress={file?.progress || 0}
                            />
                          ) : null}
                        </>
                      )}
                    </div>

                    {!fileUploaded(file) ? (
                      <div className="d-flex gap-2 align-items-center">
                        {/* <PauseInCircleIcon style={{ color: "#7E7E7E" }} /> */}
                        {[FILE_STATUS.FAILED, FILE_STATUS.RETRYING].includes(file?.status) ? (
                          <RefreshIconV2
                            style={{ color: 'var(--primary-color)' }}
                            onClick={() => handleRetry(file)}
                          />
                        ) : (
                          <CancelInCircleIcon
                            style={{ color: '#7E7E7E' }}
                            onClick={() => handleCancelSingleFile(file)}
                            disabled={!file?.data}
                          />
                        )}
                      </div>
                    ) : (
                      !hideArrow && (
                        <ArrowDownIconV2
                          style={{ transform: 'rotate(-90deg)' }}
                          onClick={() => {
                            handleOpenFile(file);
                          }}
                        />
                      )
                    )}
                  </div>
                ))}
              </body>
            ) : null}
          </div>
          {showModelLimitPopup() &&
          (limit?.models !== undefined || limit?.textures !== undefined) ? (
            <ModelLimitUpgrade
              onClose={() => setLimit({})}
              className={styles.limitPopup}
              limit={limit}
            />
          ) : null}
        </div>
      ) : null}
      {savedUploadFileData?.length > 0 ? (
        <UploadTextureModelSelect
          uploadedFiles={savedUploadFileData}
          showApplyTextureModal={showApplyTextureModal}
          setShowApplyTextureModal={setShowApplyTextureModal}
        />
      ) : null}
    </>
  );
};
